import React, { useState, useEffect } from 'react';
import {
  Button,
  View,
  Text,
  List,
  CloseButton,
  Heading,
  TextArea,
  Alert,
} from '@smarterservices/smarter-ui';
import { RadioInputGroup, RadioInput, SimpleSelect } from '@instructure/ui';
import { getStore, useCheckinStore } from '../../../stores/StoreContext';
import Modal from '../../Common/ModalContained';

/**
 * DenyConfirmation Component
 * 
 * Modal component for confirming session denial during check-in.
 * Prompts the proctor to select a reason, add notes, and choose whether
 * the test taker can reconnect or must reschedule.
 * 
 * Features:
 * - Reason selection (required)
 * - Optional notes field
 * - Reconnection option (required)
 * - Form validation
 * - Loading state during submission
 * 
 * @returns {JSX.Element} Modal with denial confirmation form
 */
const DenyConfirmation = () => {
  const CheckinStore = getStore('checkinStore');
  const LayoutStore = getStore('layoutStore');

  const isDenyConfirmationOpen = useCheckinStore(
    (state) => state.isDenyConfirmationOpen
  );

  const [selectedReason, setSelectedReason] = useState('proctor-ended');
  const [notes, setNotes] = useState('');
  const [transitionState, setTransitionState] = useState('cancel');
  const [validationError, setValidationError] = useState('');
  const [isDenying, setIsDenying] = useState(false);

  // Available denial reasons
  const [reasonOptions] = useState([
    { id: 'environment-not-compliant', label: 'Environment Not Compliant' },
    { id: 'identity-verification-failed', label: 'Identity Verification Failed' },
    { id: 'prohibited-items', label: 'Prohibited Items Detected' },
    { id: 'suspicious-behavior', label: 'Suspicious Behavior' },
    { id: 'technical-issues', label: 'Technical Issues' },
    { id: 'proctor-ended', label: 'Proctor Ended' },
    { id: 'other', label: 'Other' },
  ]);

  // Reset form when modal opens
  useEffect(() => {
    if (isDenyConfirmationOpen) {
      setSelectedReason('proctor-ended');
      setNotes('');
      setTransitionState('cancel');
      setValidationError('');
      setIsDenying(false);
    }
  }, [isDenyConfirmationOpen]);

  /**
   * Validates the denial form
   * @returns {boolean} True if form is valid, false otherwise
   */
  const validateForm = () => {
    if (!transitionState) {
      setValidationError('Please select whether the test taker can reconnect.');
      return false;
    }
    setValidationError('');
    return true;
  };

  /**
   * Handles the session denial submission
   * Validates form, sends denial data to store, and handles response
   */
  const handleDenySession = async () => {
    if (!validateForm()) {
      return;
    }

    setIsDenying(true);
    setValidationError('');

    const denyData = {
      reasonId: 'proctor-ended',
      reasonText: 'Proctor Ended',
      notes: notes,
      transitionToStatus: transitionState,
    };

    try {
      const result = await CheckinStore.denySession(denyData);

      if (result.successful) {
        // Reset form on success
        setSelectedReason('proctor-ended');
        setNotes('');
        setTransitionState('cancel');
        setValidationError('');
      } else {
        setValidationError('Failed to deny session. Please try again.');
      }
    } catch (error) {
      console.error('Error denying session:', error);
      setValidationError('An error occurred while denying the session.');
    } finally {
      setIsDenying(false);
    }
  };

  /**
   * Handles closing the modal
   * Form state is reset via useEffect when modal reopens
   */
  const handleClose = () => {
    CheckinStore.setDenyConfirmationOpen(false);
  };

  /**
   * Renders the close button for the modal header
   * @returns {JSX.Element} CloseButton component
   */
  const renderCloseButton = () => {
    return (
      <CloseButton
        placement="end"
        offset="small"
        onClick={() => handleClose()}
        screenReaderLabel="Close"
      />
    );
  };
  return (
    <Modal
      open={isDenyConfirmationOpen}
      label="Confirm Action"
      mountNode={LayoutStore.containers?.session?.element}
    >
      <Modal.Header>
        {renderCloseButton()}
        <Heading level="h3">Do you really want to deny?</Heading>
      </Modal.Header>

      <Modal.Body>
        <View as="p" margin="none none small">
          {/* Alternative options reminder */}
          <Text as="div">
            Please keep in mind you have the other options available to you.
          </Text>
          <List margin="small none">
            <List.Item>Send the test taker back to an onboarding step.</List.Item>
            <List.Item>Place them back into the waiting room.</List.Item>
          </List>

          {/* Validation error alert */}
          {validationError && (
            <Alert variant="error" margin="small none">
              {validationError}
            </Alert>
          )}

          {/* Optional notes field */}
          <View as="div" margin="small none none none">
            <TextArea
              label="Notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxHeight="10rem"
              placeholder="Please provide details for why this session is being denied."
            />
          </View>

          {/* Reconnection option 
          <View as="div" margin="small none none none">
            <RadioInputGroup
              name="transitionSessionToState"
              isRequired={true}
              defaultValue="reconnect"
              onChange={(event, value) => {
                setTransitionState(value);
                if (validationError) setValidationError('');
              }}
              description="Do you want to allow the test taker to reconnect?"
            >
              <RadioInput
                key="reconnect"
                value="reconnect"
                label="Yes, if able, they can reconnect to this appointment"
              />
              <RadioInput
                key="cancel"
                value="cancel"
                label="No, cancel the session and require rescheduling"
              />
            </RadioInputGroup>
          </View>
          */}
        </View>
      </Modal.Body>

      <Modal.Footer>
        <Button
          onClick={() => handleClose()}
          margin="0 x-small 0 0"
          interaction={isDenying ? 'disabled' : 'enabled'}
        >
          Cancel
        </Button>
        <Button
          onClick={handleDenySession}
          color="danger"
          type="submit"
          interaction={isDenying ? 'disabled' : 'enabled'}
        >
          {isDenying ? 'Denying...' : 'Yes, Deny Session'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DenyConfirmation;
